import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { openShop } from "../lib/shopCheckout";
import { useAuth } from "./AuthContext";

// The duck basket: ducks a signed-in maker wants printed, saved to their account (so it is still there on another
// device or next week). It is the source of truth for the maker's ducks in the shop cart: checking out puts exactly
// these ducks into the store's cart (pictures on every line) and the buyer pays from there.
const BasketCtx = createContext(null);

// FastAPI sends a string detail for our errors but a list of objects for validation errors: never hand a toast an object.
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" && d ? d : fallback;
};

export const BasketProvider = ({ children }) => {
  const { user, openAuth } = useAuth();
  const [basket, setBasket] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setBasket(null);
      return;
    }
    try {
      const { data } = await api.get("/basket");
      setBasket(data);
    } catch {
      setBasket(null);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = useCallback(async (fn, failMsg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setBasket(data);
      return data;
    } catch (e) {
      toast.error(errText(e, failMsg));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  // items: [{ duck_id, height?, quantity? }], one duck or a whole family at once. ensure: keep one, never add a second.
  const add = useCallback(
    async (items, { show = true, ensure = false } = {}) => {
      if (!user) {
        toast("Sign in to save ducks to your basket", { description: "Your basket is kept on your account, so you can check out later." });
        openAuth();
        return null;
      }
      const data = await run(() => api.post("/basket/items", { items, ensure }), "Could not add to your basket");
      if (data && show) {
        const n = items.length;
        toast.success(n > 1 ? `${n} ducks added to your basket` : "Added to your basket", {
          action: { label: "View basket", onClick: () => setOpen(true) },
        });
      }
      return data;
    },
    [user, openAuth, run],
  );

  const update = useCallback((itemId, patch) => run(() => api.patch(`/basket/items/${itemId}`, patch), "Could not update the basket"), [run]);
  const remove = useCallback((itemId) => run(() => api.delete(`/basket/items/${itemId}`), "Could not remove that duck"), [run]);
  const clear = useCallback(() => run(() => api.delete("/basket"), "Could not clear the basket"), [run]);

  // Put the whole basket into the store's cart and open it there.
  const checkout = useCallback(async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/basket/handoff");
      openShop(data.url);
    } catch (e) {
      toast.error(errText(e, "The shop isn't answering right now. Please try again in a moment."));
      setCheckingOut(false);
      return false;
    }
    setTimeout(() => setCheckingOut(false), 3000); // the page is navigating; reset in case the jump was blocked
    return true;
  }, []);

  // "Print this for me" on the maker's own duck: make sure it is in the basket at this size, then go to the cart.
  const printNow = useCallback(
    async (duckId, height, qrBottom = null) => {
      const data = await add([{ duck_id: duckId, height, ...(qrBottom === null ? {} : { qr_bottom: qrBottom }) }], { show: false, ensure: true });
      return data ? checkout() : false;
    },
    [add, checkout],
  );

  const value = useMemo(
    () => ({ basket, count: basket?.count || 0, open, setOpen, busy, checkingOut, refresh, add, update, remove, clear, checkout, printNow }),
    [basket, open, busy, checkingOut, refresh, add, update, remove, clear, checkout, printNow],
  );
  return <BasketCtx.Provider value={value}>{children}</BasketCtx.Provider>;
};

export const useBasket = () => useContext(BasketCtx);
